import React, { useState } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import { stopPropagation } from '../../utils/stop-propagation'
import { uniqueId } from '../../utils/unique-id'
import { useCheckedOnChange } from '../../utils/use-checked-on-change'
import { useClickOnRef } from '../../utils/use-click-on-ref'

import { RadioButtonIcons } from './radio-button-icons'
import { RadioButtonLabel } from './radio-button-label'
import { RadioButtonDescription } from './radio-button-description'

import styles from './radio-button.module.css'

const propTypes = {
    checked: PropTypes.bool,
    description: PropTypes.string,
    descriptionClassName: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    inputClassName: PropTypes.string,
    label: PropTypes.string,
    labelClassName: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.string.isRequired
}

const defaultProps = {
    checked: false,
    description: null,
    disabled: false,
    onChange() {}
}

/**
 * Rendering a radio input WITHIN a radio group.
 * Be aware that a given label (property) will overwrite any children set.
 *
 * @param {string} props.value - The html input elements value
 * @param {boolean} [props.checked] - The html input element checked state
 * @param {string} [props.className] - Classname for the li wrapper
 * @param {boolean} [props.disabled] - The html input element disabled state
 * @param {string|number} [props.id] - The input id attribute, will be generated automatically if omitted
 * @param {string} [props.inputClassName] - Classname for the html input element
 * @param {string} [props.label] - The corresponding label (you can also set it as child, see Storybook example)
 * @param {string} [props.labelClassName] - class name for html label element
 * @param {string} [props.name] - The html input element name
 * @param {Function} [props.onChange] - Change callback when input value changed
 * @param {string} [props.value] - The html input element value
 * @param {string} [props.description] - The optional description for a value
 * @returns {ReactElement} The rendered component
 */
const RadioButton = props => {
    const {
        checked: propsChecked,
        children,
        className,
        description,
        descriptionClassName,
        disabled,
        id: propsId,
        inputClassName,
        label,
        labelClassName,
        name,
        onChange,
        value
    } = props

    const [checked, onInputChanged] = useCheckedOnChange(propsChecked, onChange)
    const [inputRef, onWrapperClicked] = useClickOnRef()

    const [stateId] = useState(() => uniqueId())
    const id = propsId || stateId

    const classNameMerged = cx(
        styles['radio-button'],
        {
            [styles['checked']]: checked,
            [styles['disabled']]: disabled
        },
        className || 'radio-button',
        't3sel-radio-button'
    )

    return (
        <li data-testid="radio-button" className={classNameMerged}>
            <span className={styles['radio-button-input-label']} onClick={onWrapperClicked}>
                <input
                    type="radio"
                    ref={inputRef}
                    className={inputClassName}
                    onChange={onInputChanged}
                    onClick={stopPropagation}
                    {...{ checked, disabled, id, name, value }}
                />
                <RadioButtonIcons />
                <RadioButtonLabel className={labelClassName} {...{ id }} onClick={stopPropagation}>
                    {label || children}
                </RadioButtonLabel>
            </span>
            <RadioButtonDescription className={descriptionClassName}>{description}</RadioButtonDescription>
        </li>
    )
}

RadioButton.propTypes = propTypes
RadioButton.defaultProps = defaultProps

export { RadioButton }
